export enum MonitoramentoTipo {
  COMPETITIVIDADE_PRODUTO = 'COMPETITIVIDADE_PRODUTO',
  COMPETITIVIDADE_VAREJO = 'COMPETITIVIDADE_VAREJO',
}

export enum MonitoramentoRegraTipo {
  ENTRE = 'ENTRE',
  MENOR_IGUAL = 'MENOR_IGUAL',
  MAIOR_IGUAL = 'MAIOR_IGUAL',
}

export enum MonitoramentoPeriodo {
  ULTIMAS_4_SEMANAS = 'ULTIMAS_4_SEMANAS',
  ULTIMAS_8_SEMANAS = 'ULTIMAS_8_SEMANAS',
  ULTIMAS_12_SEMANAS = 'ULTIMAS_12_SEMANAS',
}

export type ProdutoReferencia = {
  produtoId: number;
  produtoNome: string;
  produtoEan: string;
};

export type MonitoramentoRegra = {
  id: number;
  tipo: MonitoramentoRegraTipo;
  valorMin: number | null;
  valorMax: number | null;
  ativo: boolean;
};

export type MonitoramentoDTO = {
  id: number;
  criadoEm: string;
  atualizadoEm: string;
  versao: number;
  nome: string;
  tipo: MonitoramentoTipo;
  ativo: boolean;
  usuarioLogin: string;
  tipoVinculo?: string;
  compartilhadoPorLogin?: string;
  periodo: string;
  todoTerritorio: boolean;
  tipoPreco: string;
  tipoCalculo: string;
  canalFisico: boolean;
  canalDigital: boolean;
  notificarEmail: boolean;
  notificarCentral: boolean;
  produtoReferencia: ProdutoReferencia;
  redeReferencia?: RedeFiltro;
  totalProdutosConcorrentes: number;
  totalRedes: number;
  totalCidades: number;
  cidades?: string[];
  ufs?: Array<string | null>;
  regras?: MonitoramentoRegra[];
  indiceCompetitividade?: number | null;
  indiceCompetitividadeCalculadoEm?: string;
};

export type MonitoramentoDetalheParticipanteTipo = 'REFERENCIA' | 'CONCORRENTE';

export type MonitoramentoDetalheProduto = {
  id: number;
  produtoId: number;
  produtoNome: string;
  produtoEan: string;
  tipo: MonitoramentoDetalheParticipanteTipo;
};

export type MonitoramentoDetalheRede = {
  id: number;
  redeId: number;
  redeNome: string;
  tipo: MonitoramentoDetalheParticipanteTipo;
};

// Formato inferido a partir do padrão de produtos[]/redes[] deste mesmo endpoint — ainda não confirmado
// com o back (nenhum exemplo real de monitoramento filtrado por cidade foi visto até agora).
export type MonitoramentoDetalheCidade = {
  id: number;
  cidadeId: number;
  cidadeNome: string;
  uf: string;
};

export type MonitoramentoDetalheDTO = {
  id: number;
  criadoEm: string;
  atualizadoEm: string;
  versao: number;
  nome: string;
  tipo: MonitoramentoTipo;
  ativo: boolean;
  usuarioLogin: string;
  // Mesma semântica de MonitoramentoDTO — ainda não confirmado se o GET de detalhe também retorna esses
  // campos (só vimos no response de listar até agora); tratados como opcionais por segurança.
  tipoVinculo?: string;
  compartilhadoPorLogin?: string;
  membros?: string[];
  periodo: string;
  todoTerritorio: boolean;
  tipoPreco: string;
  tipoCalculo: string;
  canalFisico: boolean;
  canalDigital: boolean;
  notificarEmail: boolean;
  notificarCentral: boolean;
  produtos: MonitoramentoDetalheProduto[];
  redes: MonitoramentoDetalheRede[];
  cidades: MonitoramentoDetalheCidade[];
  categorias: string[];
  secoes: string[];
  regras?: MonitoramentoRegra[];
  indiceCompetitividade?: number | null;
  indiceCompetitividadeCalculadoEm?: string;
};

export type MonitoramentoPageInfo = {
  size: number;
  number: number;
  totalElements: number;
  totalPages: number;
};

export type MonitoramentoPageResponse = {
  content: MonitoramentoDTO[];
  page: MonitoramentoPageInfo;
};

export type MonitoramentosListarBody = {
  tipos?: MonitoramentoTipo[];
  nome?: string;
  ativo?: boolean;
  produtoIds?: number[];
  redeIds?: number[];
  cidadeIds?: number[];
  ufs?: string[];
  emAlertaHoje?: boolean;
};

export type GetMonitoramentosParams = MonitoramentosListarBody & {
  page: number;
  size: number;
};

export type RedeFiltro = {
  redeId: number;
  redeNome: string;
};

export type CidadeFiltro = {
  cidadeId: number;
  cidadeNome: string;
  uf: string;
};

export type MonitoramentoBigNumbers = {
  totalCriados: number;
  limitePlano: number;
  alertadosUltimos30Dias: number;
  emAlertaHoje: number;
};

export enum MonitoramentoFiltroCampo {
  TIPO = 'TIPO',
  PRODUTO = 'PRODUTO',
  REDE = 'REDE',
  CIDADE = 'CIDADE',
  UF = 'UF',
}

export type MonitoramentoFiltroResponse<T> = {
  content: T[];
  page: MonitoramentoPageInfo;
};

export type CreateMonitoramentoProduto = {
  gtin: string;
  tipo: MonitoramentoDetalheParticipanteTipo;
};

export type CreateMonitoramentoRede = {
  redeNome: string;
  tipo: MonitoramentoDetalheParticipanteTipo;
};

export type CreateMonitoramentoCidade = {
  cidadeNome: string;
  uf: string;
};

export type CreateMonitoramentoRegra = {
  tipoMetrica: 'INDICE_COMPETITIVIDADE';
  tipo: MonitoramentoRegraTipo;
  valorMin: number | null;
  valorMax: number | null;
  ativo: boolean;
};

// RN1 — usuários ativos associados ao contrato do cliente (IT-858, CA-20), usados como opções pro
// campo de compartilhamento. `monitoramentosAtivos` vem do back mas não é usado pelo front hoje.
export type MonitoramentoConsumoContratoUsuario = {
  email: string;
  monitoramentosAtivos: number;
};

export type MonitoramentoConsumoContratoPageResponse = {
  content: MonitoramentoConsumoContratoUsuario[];
  totalElements: number;
};

export type MonitoramentoMembro = {
  email: string;
  compartilhadoPorLogin: string;
  compartilhadoEm: string;
};

export type CreateMonitoramentoBody = {
  nome: string;
  tipo: MonitoramentoTipo;
  periodo: string;
  todoTerritorio: boolean;
  tipoPreco: string;
  tipoCalculo: string;
  canalFisico: boolean;
  canalDigital: boolean;
  notificarEmail: boolean;
  notificarCentral: boolean;
  produtos: CreateMonitoramentoProduto[];
  redes: CreateMonitoramentoRede[];
  cidades: CreateMonitoramentoCidade[];
  regras: CreateMonitoramentoRegra[];
  categorias: string[];
  secoes: string[];
  membros: string[];
};
